<script setup lang="ts">
import type { ProductsList } from "~/models/Content/ContentProducts"
import { formProductKeys } from "~/components/Hubspot/productForms"
import { keys } from "~/utilities/injection"

export type ViewType = "list" | "detail"

const { product } = defineProps<{
  product: ProductsList
  viewType: ViewType
  align: "center" | "left"
  showStatusDetails: boolean
}>()

const { isFeide } = useAuth()

const showVariantModal = ref(false)
provide(keys.showVariantModal, showVariantModal)

const { data, isPending, error } = useEcommerceCatalogue()

const toastStore = useToastStore()

watch(error, (hasError) => {
  if (hasError) {
    toastStore.addToast({
      id: "ecommerce-catalogue-error",
      message:
          "Vi klarte ikke å hente prisinformasjonen for produktet",
      type: "error",
    })
  }
})

// Find first product that has an EAN _and_ a set price in Crystallize, as all variants should have same price.
const productVariantPrice = computed(() => {
  const prices = product.variants
    .filter(variant => !!variant.ean)
    .map(v => getProductPrice(v, data))

  return prices.find(it => typeof it?.actual === "number")
})

const hasInterestForm = computed(() => !!formProductKeys.find(it => it === product.productKey))

const hasValidLicence = computed(() => product.variants.some(p => p.userHasLicence))

const productHasFreeVariant = computed(() => product.variants.some(it => getProductPrice(it.ean, data)?.actual === 0))

const linkProps = computed(() => {
  if (product.variants.length === 1) {
    const path = `/produkter/${product.productKey ?? "exphil"}/${
      product.variants[0]?.variantKey
    }`

    return {
      to: {
        path,
        query: !hasValidLicence.value
          ? { "til-gratis-kapittel": String(true) }
          : undefined,
      },
    }
  } else {
    return {
      onClick: () => {
        showVariantModal.value = true
      },
    }
  }
})
</script>

<template>
  <div>
    <UChooseInstitute
      :product="product"
      :show-variant-modal="showVariantModal"
    />
    <div
      class="flex flex-wrap gap-x-4 lg:justify-between xl:flex-nowrap"
      :class="{
        'flex-col items-center gap-y-6 lg:items-start lg:gap-y-8':
          viewType === 'detail',
        'flex-col items-start gap-y-4 lg:flex-row':
          viewType === 'list',
        'flex-col justify-center lg:items-center': align === 'center',
      }"
    >
      <ClientOnly>
        <USimpleSkeleton v-if="isPending" />
        <div
          v-else-if="productVariantPrice && product.hasAtLeastOneReleasedProduct"
          class="flex grow flex-col gap-2 text-left text-3xl font-extrabold"
          :class="{
            'items-start gap-y-1 lg:gap-y-2': viewType === 'list',
            'items-center gap-y-2 text-marketing-mobile-p1-p1-bold-l lg:items-start lg:gap-y-4 lg:text-marketing-desktop-p1-p1-bold-l':
              viewType === 'detail',
            'lg:items-center': align === 'center',
          }"
        >
          <UPrice
            :price="productVariantPrice"
            :view-type="viewType"
          />
          <UProductDiscountedPrice
            :price="productVariantPrice"
            :product="product"
            :show-status-details="showStatusDetails"
            class="font-normal text-[--theme-darker]"
            :class="{
              'font-national2 text-lg lg:text-xl': viewType === 'list',
              'text-center font-national2condensed text-2xl lg:text-left lg:text-3xl': viewType === 'detail',
            }"
          />
        </div>
        <div
          v-else-if="viewType === 'list'"
          class="flex grow items-center italic text-[--theme-dark]"
        >
          <p
            class="font-national2condensed text-marketing-mobile-p1-p1-bold-m font-extrabold lg:text-marketing-desktop-p1-p1-bold-s"
          >
            Kommer snart
          </p>
        </div>

        <template #fallback>
          <USimpleSkeleton />
        </template>
      </ClientOnly>

      <div
        class="flex flex-wrap gap-4 md:flex-nowrap"
        :class="{
          'lg:self-end': viewType === 'list',
          'justify-center': viewType === 'detail',
        }"
      >
        <UButton
          v-if="viewType === 'list'"
          bold
          size="md"
          :to="`/produkter/${product.urlFormatProductTitle}`"
        >
          Les mer

          <span class="sr-only">om Kunne {{ product.productTitle }}</span>
        </UButton>
        <UButton
          v-if="
            product.hasAtLeastOneReleasedProduct &&
              (hasValidLicence ||
                viewType === 'detail' ||
                productHasFreeVariant)
          "
          bold
          :pill="viewType !== 'detail'"
          :theme="viewType !== 'detail' ? 'cta' : undefined"
          size="md"
          :variant="viewType !== 'detail' ? 'filled' : undefined"
          v-bind="linkProps"
        >
          {{ !hasValidLicence ? "Prøv gratis" : "Til produktet" }}

          <span class="sr-only">om Kunne {{ product.productTitle }}</span>
        </UButton>

        <UButton
          v-if="
            (product.hasAtLeastOneReleasedProduct &&
              !productHasFreeVariant &&
              !hasValidLicence) && !isFeide
          "
          variant="filled"
          theme="cta"
          pill
          size="md"
          data-pendo="forside-kjop-tilgang"
          bold
          :to="`/produkter/${product.urlFormatProductTitle}/betaling`"
        >
          Kjøp
          <span class="hidden sm:inline">tilgang</span>
          <span class="sr-only">til Kunne {{ product.productTitle }}</span>
        </UButton>
        <UButton
          v-else-if="!product.hasAtLeastOneReleasedProduct && hasInterestForm"
          :to="`/produkter/${product.urlFormatProductTitle}/registrer-interesse`"
          :data-pendo="`forside-hold-meg-oppdatert-${product.urlFormatProductTitle}`"
          variant="filled"
          theme="cta"
          size="md"
          pill
        >
          Hold meg oppdatert
        </UButton>
      </div>
    </div>
  </div>
</template>
